/*
 * vlx-shim.js
 * -----------------------------------------------------------
 * Vanilla replacement for the legacy page-builder runtime that used to be
 * loaded from a third-party CDN.
 *
 * Implements only what the theme actually uses:
 *   - Sliders (Swiper.js v11) — main + thumbnails pairing
 *   - Accordions (vanilla, ~30 lines)
 *   - Tickers / marquees (CSS animation, JS sets --duration and clones)
 *
 * Static instant types (root, container, image, text, icon, code-block,
 * grid, overlay) need no JS. The Shopify variant picker is handled by
 * the host theme. Cart-tracking events from the original script are
 * deliberately NOT reimplemented (proprietary to legacy page builder).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  // ---------------------------------------------------------
  // Sliders (+ thumbnails)
  // ---------------------------------------------------------
  function readSliderParams(el) {
    var script = document.getElementById('vlx-slider-' + el.id + '-params');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[vlx-shim] invalid slider params for', el.id, e);
      return null;
    }
  }

  function initSliders() {
    if (typeof window.Swiper === 'undefined') {
      console.warn('[vlx-shim] Swiper not loaded — skipping sliders');
      return;
    }

    var nodes = document.querySelectorAll(
      '[data-vlx-type="slider"], [data-vlx-type="thumbnails"]'
    );
    if (!nodes.length) return;

    // Group by data-vlx-slider-id so we can pair main + thumbnails.
    var groups = {};
    nodes.forEach(function (el) {
      var key = el.getAttribute('data-vlx-slider-id') || el.id;
      if (!groups[key]) groups[key] = { main: null, thumbs: null };
      if (el.getAttribute('data-vlx-type') === 'thumbnails') {
        groups[key].thumbs = el;
      } else {
        groups[key].main = el;
      }
    });

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var thumbsInstance = null;

      if (group.thumbs && !group.thumbs.__vlxSwiper) {
        var thumbsParams = readSliderParams(group.thumbs);
        if (thumbsParams) {
          try {
            thumbsInstance = new window.Swiper(group.thumbs, thumbsParams);
            group.thumbs.__vlxSwiper = thumbsInstance;
          } catch (e) {
            console.warn('[vlx-shim] thumbs init failed', key, e);
          }
        }
      }

      if (group.main && !group.main.__vlxSwiper) {
        var params = readSliderParams(group.main);
        if (!params) return;
        if (thumbsInstance) {
          if (!params.thumbs) params.thumbs = {};
          params.thumbs.swiper = thumbsInstance;
        }
        try {
          group.main.__vlxSwiper = new window.Swiper(group.main, params);
        } catch (e) {
          console.warn('[vlx-shim] slider init failed', key, e);
        }
      }
    });
  }

  // ---------------------------------------------------------
  // Accordions
  // ---------------------------------------------------------
  function initAccordions() {
    document.addEventListener('click', function (event) {
      var header = event.target.closest('[data-vlx-type="accordion-header"]');
      if (!header) return;
      var item = header.closest('[data-vlx-type="accordion-item"]');
      if (!item) return;
      var container = item.closest('[data-vlx-type="accordion-container"]');
      var isOpen = item.getAttribute('data-state') === 'open';
      var multi =
        container && container.getAttribute('data-is-multi-open-enabled') === 'true';

      if (!multi && container) {
        container
          .querySelectorAll('[data-vlx-type="accordion-item"]')
          .forEach(function (sibling) {
            if (sibling !== item) sibling.setAttribute('data-state', 'closed');
          });
      }
      item.setAttribute('data-state', isOpen ? 'closed' : 'open');
    });

    // Honor data-is-first-open on initial render.
    document
      .querySelectorAll('[data-vlx-type="accordion-container"][data-is-first-open="true"]')
      .forEach(function (container) {
        var first = container.querySelector('[data-vlx-type="accordion-item"]');
        if (first) first.setAttribute('data-state', 'open');
      });
  }

  // ---------------------------------------------------------
  // Tickers (marquee)
  // ---------------------------------------------------------
  //
  // The legacy CSS keyframe (`@keyframes vlx-ticker-vlx-scroll-XXX`)
  // translates the wrapper by `calc(-100% - var(--gap))`, which is the
  // wrapper's OWN width — i.e. it scrolls the entire clone-stack off-screen
  // every cycle, then snaps back. That's not a seamless marquee. To fix
  // this we override the animation per-ticker with a generated keyframe
  // that translates by exactly one "loop unit" (childWidth + gap), so the
  // next clone visually replaces the previous one and the loop is
  // continuous.
  var tickerKeyframesInjected = false;
  function ensureTickerKeyframes() {
    if (tickerKeyframesInjected) return;
    var styleEl = document.createElement('style');
    styleEl.setAttribute('data-vlx-shim', 'tickers');
    // Single shared keyframe — each ticker drives its own translation
    // distance via the `--shim-loop` custom property on its own element.
    styleEl.textContent =
      '@keyframes vlx-shim-ticker-scroll {' +
        ' from { transform: translate3d(0, 0, 0); }' +
        ' to { transform: translate3d(calc(-1 * var(--shim-loop, 0px)), 0, 0); }' +
      ' }';
    document.head.appendChild(styleEl);
    tickerKeyframesInjected = true;
  }

  function initTickers() {
    ensureTickerKeyframes();
    var tickers = document.querySelectorAll('[data-vlx-type="ticker"]');
    tickers.forEach(function (container) {
      if (container.__vlxTickerInit) return;
      container.__vlxTickerInit = true;

      var inner = container.querySelector('.vlx-ticker');
      var initialChild = container.querySelector('.vlx-ticker-initial-child-container');
      if (!inner || !initialChild) return;

      var speed = parseFloat(container.getAttribute('data-vlx-ticker-speed')) || 50;
      var reversed = container.getAttribute('data-vlx-ticker-is-reversed') === 'true';
      var paused = container.getAttribute('data-vlx-ticker-pause') === 'true';

      var cloneAndMeasure = function () {
        // Reset to a single original child before re-cloning.
        var clones = inner.querySelectorAll('.vlx-ticker-initial-child-container');
        for (var i = clones.length - 1; i > 0; i--) clones[i].remove();

        var containerWidth = container.offsetWidth || window.innerWidth;
        var childWidth = initialChild.offsetWidth;
        if (!childWidth || childWidth <= 0) childWidth = containerWidth;

        // Read column-gap from the computed style of the inner wrapper.
        // The legacy CSS sets `column-gap:inherit` which inherits from the
        // ticker container (often 0).
        var computedGap = parseFloat(getComputedStyle(inner).columnGap) || 0;
        var loopUnit = childWidth + computedGap;

        // Need enough copies so the wrapper is at least 2x the container,
        // PLUS one extra child to cover the loop reset moment.
        var copiesNeeded = Math.ceil((containerWidth + loopUnit) / loopUnit) + 1;
        for (var j = 1; j < copiesNeeded; j++) {
          inner.appendChild(initialChild.cloneNode(true));
        }

        var durationSec = loopUnit / speed;

        // Override only animation-name (not the shorthand) so the legacy
        // CSS rule's `animation-direction: var(--direction)` and
        // `animation-play-state: var(--play)` still apply, which preserves
        // the hover/click-pause behavior driven by `.vlx-ticker-container:hover div`.
        // The legacy shorthand's `var(--duration)` is still respected too.
        inner.style.animationName = 'vlx-shim-ticker-scroll';
        inner.style.willChange = 'transform';

        inner.style.setProperty('--shim-loop', loopUnit + 'px');
        inner.style.setProperty('--duration', durationSec + 's');
        inner.style.setProperty('--direction', reversed ? 'reverse' : 'normal');
        // --play lives on the container so the :hover/:active rules
        // (`.vlx-ticker-container:hover div`) can read sibling vars
        // through inheritance.
        container.style.setProperty('--play', paused ? 'paused' : 'running');
        if (!container.style.getPropertyValue('--pause-on-hover')) {
          container.style.setProperty('--pause-on-hover', 'running');
        }
        if (!container.style.getPropertyValue('--pause-on-click')) {
          container.style.setProperty('--pause-on-click', 'running');
        }
      };

      cloneAndMeasure();

      // Re-measure on resize (debounced to avoid thrash).
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(cloneAndMeasure, 150);
      });
    });
  }

  // ---------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------
  ready(function () {
    initAccordions();
    initSliders();
    initTickers();
  });

  // Re-init on Shopify section reloads (theme editor).
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      initSliders();
      initTickers();
    });
  }
})();
